import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: { padding: 40, fontSize: 11, fontFamily: 'Helvetica', backgroundColor: '#ffffff' },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 30 },
  companyInfo: { textAlign: 'right', maxWidth: '50%' },
  companyName: { fontSize: 14, fontWeight: 'bold', marginBottom: 3 },
  companyDetails: { fontSize: 9, color: '#666', marginBottom: 2 },
  titleSection: { marginBottom: 20 },
  title: { fontSize: 28, fontWeight: 'bold', color: '#10b981', marginBottom: 4 },
  cnNumber: { fontSize: 12, color: '#666' },
  statusBadge: { fontSize: 10, color: '#ffffff', backgroundColor: '#10b981', padding: '4 10', borderRadius: 4, marginTop: 6, alignSelf: 'flex-start' },
  infoRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 20, padding: 16, backgroundColor: '#f9fafb', borderRadius: 8 },
  infoBlock: { width: '48%' },
  label: { fontSize: 9, color: '#888', marginBottom: 3, textTransform: 'uppercase', fontWeight: 'bold' },
  value: { fontSize: 11, marginBottom: 4 },
  divider: { borderBottomWidth: 1, borderColor: '#e5e7eb', marginVertical: 16 },
  amountBox: { backgroundColor: '#ecfdf5', borderWidth: 2, borderColor: '#10b981', borderRadius: 8, padding: 20, alignItems: 'center', marginVertical: 20 },
  amountLabel: { fontSize: 12, color: '#065f46', marginBottom: 8, fontWeight: 'bold' },
  amountValue: { fontSize: 32, fontWeight: 'bold', color: '#10b981' },
  amountNote: { fontSize: 10, color: '#6b7280', marginTop: 6 },
  reasonBox: { backgroundColor: '#fffbeb', borderWidth: 1, borderColor: '#fbbf24', borderRadius: 6, padding: 12, marginBottom: 16 },
  reasonLabel: { fontSize: 9, color: '#92400e', fontWeight: 'bold', marginBottom: 4, textTransform: 'uppercase' },
  reasonText: { fontSize: 11, color: '#78350f' },
  table: { marginTop: 12, marginBottom: 20 },
  tableHeader: { flexDirection: 'row', backgroundColor: '#10b981', padding: 8, fontWeight: 'bold', fontSize: 9 },
  tableHeaderText: { color: '#ffffff' },
  tableRow: { flexDirection: 'row', paddingVertical: 8, paddingHorizontal: 8, borderBottomWidth: 1, borderColor: '#f0f0f0', fontSize: 10 },
  col1: { width: '50%' },
  col2: { width: '20%', textAlign: 'center' },
  col3: { width: '15%', textAlign: 'right' },
  col4: { width: '15%', textAlign: 'right', fontWeight: 'bold' },
  totalsSection: { alignItems: 'flex-end', marginTop: 8 },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', width: '45%', paddingVertical: 5 },
  grandTotal: { backgroundColor: '#10b981', padding: '8 12', borderRadius: 6, marginTop: 4 },
  grandTotalText: { color: '#ffffff', fontWeight: 'bold', fontSize: 13 },
  remainingBox: { backgroundColor: '#f0fdf4', borderWidth: 1, borderColor: '#86efac', borderRadius: 6, padding: 10, marginTop: 8 },
  applicationHistory: { marginTop: 16 },
  historyTitle: { fontSize: 10, fontWeight: 'bold', color: '#374151', marginBottom: 8, textTransform: 'uppercase' },
  historyRow: { flexDirection: 'row', justifyContent: 'space-between', fontSize: 10, paddingVertical: 4, borderBottomWidth: 1, borderColor: '#f3f4f6' },
  footer: { position: 'absolute', bottom: 30, left: 40, right: 40, textAlign: 'center', fontSize: 9, color: '#9ca3af', borderTopWidth: 1, borderColor: '#e5e7eb', paddingTop: 10 },
});

const CreditNotePDF: React.FC<{ creditNote: any; businessSettings?: any; applicationHistory?: any[] }> = ({
  creditNote,
  businessSettings,
  applicationHistory = [],
}) => {
  const cn = creditNote;
  const issueDate = cn.issued_at || cn.created_at
    ? new Date(cn.issued_at || cn.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  const totalAmount = parseFloat(cn.amount || 0);
  const remaining = parseFloat(cn.remaining_balance || cn.amount || 0);
  const applied = totalAmount - remaining;

  const statusColors: Record<string, string> = {
    active: '#10b981',
    fully_applied: '#3b82f6',
    voided: '#ef4444',
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.headerRow}>
          <View style={styles.titleSection}>
            <Text style={styles.title}>CREDIT NOTE</Text>
            <Text style={styles.cnNumber}>CN-{cn.id || 'N/A'}</Text>
            <View style={[styles.statusBadge, { backgroundColor: statusColors[cn.status] || '#10b981' }]}>
              <Text style={{ color: '#fff', fontSize: 9 }}>{(cn.status || 'active').replace('_', ' ').toUpperCase()}</Text>
            </View>
          </View>
          <View style={styles.companyInfo}>
            {businessSettings?.name && <Text style={styles.companyName}>{businessSettings.name}</Text>}
            {businessSettings?.email && <Text style={styles.companyDetails}>{businessSettings.email}</Text>}
            {businessSettings?.phone && <Text style={styles.companyDetails}>Tel: {businessSettings.phone}</Text>}
            {businessSettings?.website && <Text style={styles.companyDetails}>{businessSettings.website}</Text>}
            {businessSettings?.companyNumber && <Text style={styles.companyDetails}>Company: {businessSettings.companyNumber}</Text>}
            {businessSettings?.vatNumber && <Text style={styles.companyDetails}>VAT: {businessSettings.vatNumber}</Text>}
          </View>
        </View>

        {/* Info Grid */}
        <View style={styles.infoRow}>
          <View style={styles.infoBlock}>
            <Text style={styles.label}>Credit Note Date</Text>
            <Text style={styles.value}>{issueDate}</Text>
            {cn.invoice_number && (
              <>
                <Text style={styles.label}>Related Invoice</Text>
                <Text style={styles.value}>Invoice #{cn.invoice_number}</Text>
              </>
            )}
          </View>
          <View style={styles.infoBlock}>
            <Text style={styles.label}>Issued To</Text>
            <Text style={styles.value}>{cn.customer_name || 'N/A'}</Text>
            {cn.customer_email && <Text style={[styles.value, { color: '#6b7280', fontSize: 10 }]}>{cn.customer_email}</Text>}
          </View>
        </View>

        {/* Reason */}
        {cn.reason && (
          <View style={styles.reasonBox}>
            <Text style={styles.reasonLabel}>Reason for Credit</Text>
            <Text style={styles.reasonText}>{cn.reason}</Text>
          </View>
        )}

        {/* Credit Amount */}
        <View style={styles.amountBox}>
          <Text style={styles.amountLabel}>TOTAL CREDIT AMOUNT</Text>
          <Text style={styles.amountValue}>£{totalAmount.toFixed(2)}</Text>
          {applied > 0 && (
            <Text style={styles.amountNote}>Applied to invoices: £{applied.toFixed(2)} | Remaining: £{remaining.toFixed(2)}</Text>
          )}
          {applied === 0 && (
            <Text style={styles.amountNote}>Available for use against future invoices</Text>
          )}
        </View>

        {/* Summary Totals */}
        <View style={styles.totalsSection}>
          <View style={styles.totalRow}>
            <Text style={{ color: '#6b7280' }}>Credit Issued:</Text>
            <Text>£{totalAmount.toFixed(2)}</Text>
          </View>
          {applied > 0 && (
            <View style={styles.totalRow}>
              <Text style={{ color: '#6b7280' }}>Applied:</Text>
              <Text style={{ color: '#ef4444' }}>-£{applied.toFixed(2)}</Text>
            </View>
          )}
          <View style={[styles.totalRow, styles.grandTotal]}>
            <Text style={styles.grandTotalText}>Remaining Balance:</Text>
            <Text style={styles.grandTotalText}>£{remaining.toFixed(2)}</Text>
          </View>
        </View>

        {/* Application History */}
        {applicationHistory.length > 0 && (
          <View style={styles.applicationHistory}>
            <Text style={styles.historyTitle}>Application History</Text>
            {applicationHistory.map((app: any, i: number) => (
              <View key={i} style={styles.historyRow}>
                <Text style={{ width: '35%' }}>Invoice #{app.invoice_number}</Text>
                <Text style={{ width: '35%', color: '#6b7280' }}>{app.applied_by_name || 'Staff'}</Text>
                <Text style={{ width: '15%', color: '#6b7280', fontSize: 9 }}>
                  {app.applied_at ? new Date(app.applied_at).toLocaleDateString('en-GB') : ''}
                </Text>
                <Text style={{ width: '15%', textAlign: 'right', color: '#ef4444' }}>-£{parseFloat(app.amount_applied || 0).toFixed(2)}</Text>
              </View>
            ))}
          </View>
        )}

        {/* Void Reason */}
        {cn.status === 'voided' && cn.void_reason && (
          <View style={[styles.reasonBox, { backgroundColor: '#fef2f2', borderColor: '#fca5a5', marginTop: 12 }]}>
            <Text style={[styles.reasonLabel, { color: '#991b1b' }]}>Void Reason</Text>
            <Text style={[styles.reasonText, { color: '#7f1d1d' }]}>{cn.void_reason}</Text>
          </View>
        )}

        <View style={styles.footer}>
          <Text>This is an official credit note issued by {businessSettings?.name || 'the company'}.</Text>
          <Text>Please retain for your records. For queries contact: {businessSettings?.email || ''}</Text>
        </View>
      </Page>
    </Document>
  );
};

export default CreditNotePDF;
